import { useEffect, useState } from "react";
import axios from "axios";
import { X, ListChecks, Briefcase, Clock, ArrowRight, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API_BASE } from "./AuthContext";

/**
 * Side-panel modal: shows a teammate's open tasks + open jobs.
 * Triggered by clicking a row on the staff-workload card.
 */
export default function TeammateDrillThrough({ open, user, onClose, members = [] }) {
  const [tasks, setTasks] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rebalanceOpen, setRebalanceOpen] = useState(false);
  const [rebalanceBusy, setRebalanceBusy] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API_BASE}/tasks?assignee_id=${user.user_id}&status=open`),
      axios.get(`${API_BASE}/jobs?assignee_id=${user.user_id}`),
    ])
      .then(([t, j]) => {
        setTasks(t.data || []);
        setJobs((j.data || []).filter((x) => x.status !== "done"));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, user]);

  if (!open || !user) return null;

  const rebalanceTo = async (targetUserId) => {
    if (tasks.length === 0) {
      toast.error("No open tasks to reassign");
      return;
    }
    setRebalanceBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/tasks/bulk`, {
        action: "reassign",
        task_ids: tasks.map((t) => t.task_id),
        assignee_id: targetUserId || null,
      });
      const targetLabel = targetUserId
        ? (members.find((m) => m.user_id === targetUserId)?.name || "teammate")
        : "Unassigned";
      toast.success(`${r.data.affected} task(s) → ${targetLabel}`);
      setRebalanceOpen(false);
      window.dispatchEvent(new Event("tasks:refresh-pending"));
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rebalance failed");
    } finally {
      setRebalanceBusy(false);
    }
  };

  const fmtHM = (mins) => {
    const h = Math.floor((mins || 0) / 60);
    const m = (mins || 0) % 60;
    return h ? `${h}h ${m}m` : `${m}m`;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/30"
      onClick={onClose}
      data-testid="teammate-drill-modal"
    >
      <div
        className="w-full sm:max-w-md h-full shadow-xl flex flex-col"
        style={{ background: "var(--paper)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="overline">Workload · {user.role || "teammate"}</div>
            <div className="text-[16px] font-medium text-[var(--ink)]" data-testid="teammate-drill-name">{user.name || user.email}</div>
            <div className="text-[11px] text-[var(--ink-3)] mt-0.5">
              {user.open_jobs} jobs · {user.open_tasks} tasks · {fmtHM(user.minutes_this_week)} this week
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="teammate-drill-close">
            <X size={18}/>
          </button>
        </div>

        {tasks.length > 0 && (
          <div className="px-5 py-2 border-b flex items-center gap-2 relative" style={{ borderColor: "var(--border)", background: "var(--subtle)" }}>
            <div className="text-[11px] text-[var(--ink-2)] flex-1">
              <Users size={11} className="inline mr-1"/> Rebalance {tasks.length} open task{tasks.length === 1 ? "" : "s"} to a teammate?
            </div>
            <button
              type="button"
              onClick={() => setRebalanceOpen((v) => !v)}
              disabled={rebalanceBusy}
              data-testid="teammate-drill-rebalance"
              className="px-3 py-1 rounded text-[12px] font-medium disabled:opacity-50"
              style={{ background: "var(--ink)", color: "var(--paper)" }}
            >
              {rebalanceBusy ? "Reassigning…" : "Rebalance →"}
            </button>
            {rebalanceOpen && (
              <div
                className="absolute right-5 top-full mt-1 w-56 max-h-72 overflow-y-auto rounded-md border shadow-md z-10"
                style={{ background: "var(--paper)", borderColor: "var(--border)" }}
                data-testid="teammate-drill-rebalance-menu"
              >
                {members.filter((m) => m.user_id !== user.user_id).map((m) => (
                  <button
                    key={m.user_id}
                    type="button"
                    onClick={() => rebalanceTo(m.user_id)}
                    className="w-full text-left px-3 py-2 text-[12px] hover:bg-[var(--subtle)]"
                    data-testid={`teammate-drill-rebalance-to-${m.user_id}`}
                  >
                    {m.name} <span className="text-[var(--ink-3)] text-[10px]">{m.role}</span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => rebalanceTo(null)}
                  className="w-full text-left px-3 py-2 text-[12px] hover:bg-[var(--subtle)] border-t"
                  style={{ borderColor: "var(--border)" }}
                  data-testid="teammate-drill-rebalance-unassign"
                >— Unassign all —</button>
              </div>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading && <div className="text-[var(--ink-3)] text-[12px]">Loading…</div>}

          {!loading && (
            <>
              <section data-testid="teammate-drill-tasks">
                <div className="flex items-center justify-between mb-2">
                  <div className="overline inline-flex items-center gap-1"><ListChecks size={12}/> Open tasks ({tasks.length})</div>
                  <Link to={`/tasks`} className="text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)] inline-flex items-center gap-0.5">
                    Open page <ArrowRight size={10}/>
                  </Link>
                </div>
                {tasks.length === 0 ? (
                  <div className="text-[12px] text-[var(--ink-3)] italic">No open tasks.</div>
                ) : (
                  <div className="space-y-1.5">
                    {tasks.slice(0, 20).map((t) => (
                      <div key={t.task_id} className="px-2.5 py-2 rounded text-[13px]" style={{ background: "var(--subtle)" }} data-testid={`teammate-drill-task-${t.task_id}`}>
                        <div className="text-[var(--ink)] truncate">{t.title}</div>
                        <div className="flex items-center gap-2 text-[10px] text-[var(--ink-3)] mt-0.5">
                          {t.due_at && (<span><Clock size={9} className="inline mr-0.5"/>{new Date(t.due_at).toLocaleDateString()}</span>)}
                          {t.priority !== "medium" && <span>· {t.priority}</span>}
                          {t.parent_kind && t.parent_kind !== "none" && <span>· {t.parent_kind}</span>}
                        </div>
                      </div>
                    ))}
                    {tasks.length > 20 && (
                      <div className="text-[10px] text-[var(--ink-3)] mt-1">+ {tasks.length - 20} more</div>
                    )}
                  </div>
                )}
              </section>

              <section data-testid="teammate-drill-jobs">
                <div className="flex items-center justify-between mb-2">
                  <div className="overline inline-flex items-center gap-1"><Briefcase size={12}/> Open jobs ({jobs.length})</div>
                  <Link to={`/jobs`} className="text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)] inline-flex items-center gap-0.5">
                    Open page <ArrowRight size={10}/>
                  </Link>
                </div>
                {jobs.length === 0 ? (
                  <div className="text-[12px] text-[var(--ink-3)] italic">No open jobs.</div>
                ) : (
                  <div className="space-y-1.5">
                    {jobs.slice(0, 20).map((j) => (
                      <div key={j.job_id} className="px-2.5 py-2 rounded text-[13px]" style={{ background: "var(--subtle)" }} data-testid={`teammate-drill-job-${j.job_id}`}>
                        <div className="text-[var(--ink)] truncate">{j.title}</div>
                        <div className="flex items-center gap-2 text-[10px] text-[var(--ink-3)] mt-0.5">
                          <span className="capitalize">{j.status?.replace(/_/g, " ")}</span>
                          {j.priority && j.priority !== "medium" && <span>· {j.priority}</span>}
                          {j.due_at && <span>· due {new Date(j.due_at).toLocaleDateString()}</span>}
                        </div>
                      </div>
                    ))}
                    {jobs.length > 20 && (
                      <div className="text-[10px] text-[var(--ink-3)] mt-1">+ {jobs.length - 20} more</div>
                    )}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
